/**
 * Comprehensive Club Specifications Database
 * 
 * Rich data for accurate club recommendations and fitting
 * Enables fitter-quality suggestions based on player profile
 */

export interface ClubSpecs {
  id: string;
  brand: string;
  model: string;
  year: number;
  category: 'driver' | 'fairway-wood' | 'hybrid' | 'iron-set' | 'wedge' | 'putter';
  
  // Performance Characteristics
  launch: 'low' | 'mid-low' | 'mid' | 'mid-high' | 'high';
  spin: 'low' | 'mid-low' | 'mid' | 'mid-high' | 'high';
  forgiveness: number; // 1-10 scale (10 = most forgiving)
  workability: number; // 1-10 scale (10 = most workable)
  bias: 'draw' | 'neutral' | 'fade' | 'slight-draw' | 'slight-fade';
  
  // Player Type Targeting
  playerType: 'tour' | 'better-player' | 'game-improvement' | 'super-game-improvement' | 'beginner';
  handicapRange: string; // e.g., "0-5", "5-15", "15+"
  swingSpeedRange: string; // e.g., "95-105 mph", "85-95 mph"
  
  // Construction
  headType?: 'blade' | 'cavity-back' | 'hollow-body' | 'muscle-back' | 'players-distance';
  faceType?: 'forged' | 'cast' | 'multi-material' | 'titanium' | 'steel';
  
  // Technologies
  keyTechnologies: string[]; // e.g., ["Carbon Crown", "Twist Face", "Speed Pocket"]
  
  // Specs
  lofts?: number[]; // Available lofts
  shaftOptions?: string[]; // Stock shaft offerings
  priceRange: string; // e.g., "$400-500", "$200-300"
  
  // Ratings
  distanceRating?: number; // 1-10
  feelRating?: number; // 1-10
  soundRating?: number; // 1-10
  
  // Notes
  bestFor: string[]; // e.g., ["slice correction", "distance", "accuracy"]
  notIdealFor?: string[]; // e.g., ["low handicaps", "slow swingers"]
  
  // Availability
  isCurrentModel: boolean;
  releaseDate?: string;
}

/**
 * DRIVERS
 */
export const DRIVER_SPECS: ClubSpecs[] = [
  {
    id: 'taylormade-stealth-2-2023',
    brand: 'TaylorMade',
    model: 'Stealth 2',
    year: 2023,
    category: 'driver',
    launch: 'mid-high',
    spin: 'mid-low',
    forgiveness: 8,
    workability: 6,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '5-20',
    swingSpeedRange: '90-110 mph',
    faceType: 'titanium',
    keyTechnologies: ['Carbon Crown', 'Twist Face', '60X Carbon Twist Face'],
    lofts: [9, 10.5, 12],
    shaftOptions: ['Fujikura Ventus TR Red', 'Project X HZRDUS Smoke'],
    priceRange: '$500-600',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 8,
    bestFor: ['distance', 'forgiveness', 'mid-handicappers'],
    notIdealFor: ['tour players seeking max workability'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'callaway-paradym-2023',
    brand: 'Callaway',
    model: 'Paradym',
    year: 2023,
    category: 'driver',
    launch: 'mid',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-15',
    swingSpeedRange: '95-115 mph',
    faceType: 'titanium',
    keyTechnologies: ['360° Carbon Chassis', 'Jailbreak AI Speed Frame', 'Forged Titanium Face'],
    lofts: [9, 10.5, 12],
    shaftOptions: ['Project X Cypher', 'Mitsubishi Tensei AV Raw'],
    priceRange: '$550-650',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 9,
    bestFor: ['distance', 'consistency', 'tour-level performance'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'ping-g430-max-2023',
    brand: 'Ping',
    model: 'G430 MAX',
    year: 2023,
    category: 'driver',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '10-25',
    swingSpeedRange: '85-105 mph',
    faceType: 'titanium',
    keyTechnologies: ['Carbon Fly Wrap', 'Spinsistency', 'Carbonfly Wrap Crown'],
    lofts: [9, 10.5, 12],
    shaftOptions: ['PING ALTA CB Black', 'Mitsubishi Tensei AV Raw White'],
    priceRange: '$500-600',
    distanceRating: 8,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['forgiveness', 'slice correction', 'high handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'titleist-tsi3-2021',
    brand: 'Titleist',
    model: 'TSi3',
    year: 2021,
    category: 'driver',
    launch: 'mid-low',
    spin: 'low',
    forgiveness: 6,
    workability: 9,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-8',
    swingSpeedRange: '100-120 mph',
    faceType: 'titanium',
    keyTechnologies: ['ATI 425 Face', 'SureFit CG', 'Aerodynamic Head Shape'],
    lofts: [8, 9, 10, 11],
    shaftOptions: ['Project X HZRDUS Smoke Black', 'Mitsubishi Diamana D+ Limited'],
    priceRange: '$400-500',
    distanceRating: 8,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['workability', 'low spin', 'tour players'],
    notIdealFor: ['high handicappers', 'slice tendency'],
    isCurrentModel: false,
    releaseDate: '2021-09'
  },
  // 2024 DRIVERS
  {
    id: 'taylormade-qi10-max-2024',
    brand: 'TaylorMade',
    model: 'Qi10 MAX',
    year: 2024,
    category: 'driver',
    launch: 'high',
    spin: 'mid',
    forgiveness: 10,
    workability: 4,
    bias: 'slight-draw',
    playerType: 'super-game-improvement',
    handicapRange: '15-36',
    swingSpeedRange: '75-100 mph',
    faceType: 'titanium',
    keyTechnologies: ['60X Carbon Twist Face', 'Infinity Carbon Crown', 'Sliding Weight Track'],
    lofts: [9, 10.5, 12],
    shaftOptions: ['Fujikura Speeder NX', 'Project X HZRDUS Black'],
    priceRange: '$580-650',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 8,
    bestFor: ['max forgiveness', 'slice correction', 'high handicappers'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'taylormade-qi10-ls-2024',
    brand: 'TaylorMade',
    model: 'Qi10 LS',
    year: 2024,
    category: 'driver',
    launch: 'mid-low',
    spin: 'low',
    forgiveness: 6,
    workability: 9,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-8',
    swingSpeedRange: '105-125 mph',
    faceType: 'titanium',
    keyTechnologies: ['60X Carbon Twist Face', 'Low Spin Design', 'Adjustable Hosel'],
    lofts: [8, 9, 10.5],
    shaftOptions: ['Project X HZRDUS Black', 'Mitsubishi Kai\'li White'],
    priceRange: '$580-650',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['low spin', 'workability', 'fast swing speeds'],
    notIdealFor: ['high handicappers', 'slice tendency'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'callaway-paradym-ai-smoke-max-2024',
    brand: 'Callaway',
    model: 'Paradym Ai Smoke MAX',
    year: 2024,
    category: 'driver',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-25',
    swingSpeedRange: '85-105 mph',
    faceType: 'titanium',
    keyTechnologies: ['Ai Smart Face', '360° Carbon Chassis', 'Jailbreak AI'],
    lofts: [9, 10.5, 12],
    shaftOptions: ['Project X Cypher 2.0', 'Mitsubishi Tensei AV Raw Blue'],
    priceRange: '$550-650',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 9,
    bestFor: ['forgiveness', 'consistency', 'mid-handicappers'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'callaway-paradym-ai-smoke-triple-diamond-2024',
    brand: 'Callaway',
    model: 'Paradym Ai Smoke Triple Diamond',
    year: 2024,
    category: 'driver',
    launch: 'mid-low',
    spin: 'low',
    forgiveness: 5,
    workability: 10,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-5',
    swingSpeedRange: '105-125 mph',
    faceType: 'titanium',
    keyTechnologies: ['Ai Smart Face', 'Tour Shape', 'Adjustable Weights'],
    lofts: [8, 9, 10.5],
    shaftOptions: ['Project X HZRDUS Black', 'Mitsubishi Tensei Pro White'],
    priceRange: '$600-700',
    distanceRating: 8,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['workability', 'low spin', 'tour players'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'ping-g430-lst-2023',
    brand: 'Ping',
    model: 'G430 LST',
    year: 2023,
    category: 'driver',
    launch: 'mid-low',
    spin: 'low',
    forgiveness: 7,
    workability: 8,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-12',
    swingSpeedRange: '100-120 mph',
    faceType: 'titanium',
    keyTechnologies: ['Carbon Fly Wrap', 'Spinsistency', 'Low Spin Technology'],
    lofts: [9, 10.5],
    shaftOptions: ['PING ALTA CB Black', 'Mitsubishi Tensei AV Raw White'],
    priceRange: '$500-600',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['low spin', 'fast swingers', 'distance'],
    notIdealFor: ['high handicappers', 'slow swingers'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'titleist-gt2-2024',
    brand: 'Titleist',
    model: 'GT2',
    year: 2024,
    category: 'driver',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-15',
    swingSpeedRange: '90-115 mph',
    faceType: 'titanium',
    keyTechnologies: ['Speed Ring VFT', 'Low-CG Carbon Sole', 'SureFit Adjustability'],
    lofts: [9, 10, 11],
    shaftOptions: ['Project X HZRDUS Black', 'Mitsubishi Tensei Pro Orange'],
    priceRange: '$600-700',
    distanceRating: 9,
    feelRating: 9,
    soundRating: 10,
    bestFor: ['distance', 'forgiveness with tour feel', 'better players'],
    isCurrentModel: true,
    releaseDate: '2024-09'
  },
  {
    id: 'titleist-gt3-2024',
    brand: 'Titleist',
    model: 'GT3',
    year: 2024,
    category: 'driver',
    launch: 'mid',
    spin: 'mid-low',
    forgiveness: 7,
    workability: 9,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-8',
    swingSpeedRange: '100-120 mph',
    faceType: 'titanium',
    keyTechnologies: ['Speed Ring VFT', 'Adjustable CG Track', 'Premium Sound'],
    lofts: [8, 9, 10],
    shaftOptions: ['Project X HZRDUS Black', 'Mitsubishi Diamana S+ Limited'],
    priceRange: '$600-700',
    distanceRating: 8,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['workability', 'tour-level control', 'shot shaping'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2024-09'
  },
  {
    id: 'cobra-darkspeed-max-2024',
    brand: 'Cobra',
    model: 'DARKSPEED MAX',
    year: 2024,
    category: 'driver',
    launch: 'high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'draw',
    playerType: 'game-improvement',
    handicapRange: '10-30',
    swingSpeedRange: '80-100 mph',
    faceType: 'titanium',
    keyTechnologies: ['PWRShell L-Cup Face', 'H.O.T Face AI', 'Carbon Wrap Crown'],
    lofts: [9, 10.5, 12],
    shaftOptions: ['Arccos Caddie Smart Grips', 'Project X HZRDUS Gen4'],
    priceRange: '$450-550',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['draw bias', 'forgiveness', 'value'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'cobra-darkspeed-x-2024',
    brand: 'Cobra',
    model: 'DARKSPEED X',
    year: 2024,
    category: 'driver',
    launch: 'mid',
    spin: 'mid-low',
    forgiveness: 7,
    workability: 8,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-12',
    swingSpeedRange: '95-115 mph',
    faceType: 'titanium',
    keyTechnologies: ['PWRShell L-Cup Face', 'H.O.T Face AI', 'Adjustable Weights'],
    lofts: [9, 10.5],
    shaftOptions: ['Project X HZRDUS Gen4', 'Mitsubishi Tensei Pro'],
    priceRange: '$450-550',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'workability', 'value for performance'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'mizuno-st-max-230-2023',
    brand: 'Mizuno',
    model: 'ST-MAX 230',
    year: 2023,
    category: 'driver',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-25',
    swingSpeedRange: '80-100 mph',
    faceType: 'titanium',
    keyTechnologies: ['Cortech Chamber', 'Beta Rich Titanium Face', 'Carbon Crown'],
    lofts: [9.5, 10.5, 12],
    shaftOptions: ['MFUSION M', 'Project X HZRDUS Smoke'],
    priceRange: '$450-550',
    distanceRating: 8,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['forgiveness', 'high launch', 'slower swing speeds'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'mizuno-st-z-230-2023',
    brand: 'Mizuno',
    model: 'ST-Z 230',
    year: 2023,
    category: 'driver',
    launch: 'mid',
    spin: 'mid-low',
    forgiveness: 7,
    workability: 8,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-12',
    swingSpeedRange: '95-115 mph',
    faceType: 'titanium',
    keyTechnologies: ['Cortech Chamber', 'Carbon Crown', 'Quick Switch Hosel'],
    lofts: [9, 10.5],
    shaftOptions: ['Project X HZRDUS Smoke', 'Mitsubishi Tensei AV'],
    priceRange: '$450-550',
    distanceRating: 9,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['workability', 'low spin', 'feel-focused players'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'srixon-zx5-mkii-2023',
    brand: 'Srixon',
    model: 'ZX5 MK II',
    year: 2023,
    category: 'driver',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-18',
    swingSpeedRange: '90-110 mph',
    faceType: 'titanium',
    keyTechnologies: ['Rebound Frame', 'Carbon Crown', 'Dual Speed Shaft'],
    lofts: [9.5, 10.5],
    shaftOptions: ['Project X HZRDUS Smoke', 'Mitsubishi Diamana PD'],
    priceRange: '$450-550',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'forgiveness', 'mid-handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-03'
  },
  {
    id: 'srixon-zx7-mkii-2023',
    brand: 'Srixon',
    model: 'ZX7 MK II',
    year: 2023,
    category: 'driver',
    launch: 'mid',
    spin: 'mid-low',
    forgiveness: 6,
    workability: 9,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-10',
    swingSpeedRange: '100-120 mph',
    faceType: 'titanium',
    keyTechnologies: ['Rebound Frame', 'Tour Shape', 'Adjustable Weights'],
    lofts: [8.5, 9.5, 10.5],
    shaftOptions: ['Project X HZRDUS Black', 'Mitsubishi Tensei Pro'],
    priceRange: '$450-550',
    distanceRating: 8,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['workability', 'tour players', 'shot shaping'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-03'
  },
  {
    id: 'cleveland-launcher-xl2-2023',
    brand: 'Cleveland',
    model: 'Launcher XL2',
    year: 2023,
    category: 'driver',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 10,
    workability: 4,
    bias: 'draw',
    playerType: 'super-game-improvement',
    handicapRange: '15-36',
    swingSpeedRange: '70-95 mph',
    faceType: 'titanium',
    keyTechnologies: ['Rebound Frame', 'Action Mass CB', 'Draw Bias Design'],
    lofts: [9, 10.5, 12],
    shaftOptions: ['Miyazaki KAILI', 'Project X Cypher'],
    priceRange: '$350-450',
    distanceRating: 8,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['max forgiveness', 'slice fix', 'beginners'],
    notIdealFor: ['low handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
];

/**
 * FAIRWAY WOODS
 */
export const FAIRWAY_WOOD_SPECS: ClubSpecs[] = [
  {
    id: 'taylormade-qi10-3wood-2024',
    brand: 'TaylorMade',
    model: 'Qi10 3-Wood',
    year: 2024,
    category: 'fairway-wood',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 9,
    workability: 6,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '5-25',
    swingSpeedRange: '85-105 mph',
    faceType: 'steel',
    keyTechnologies: ['Carbon Crown', 'Speed Pocket', 'Twist Face'],
    lofts: [13.5, 15, 16.5, 18, 21],
    shaftOptions: ['Fujikura Speeder NX', 'Project X HZRDUS Black'],
    priceRange: '$300-400',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 8,
    bestFor: ['distance', 'forgiveness', 'off-the-tee versatility'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'callaway-paradym-ai-smoke-max-3wood-2024',
    brand: 'Callaway',
    model: 'Paradym Ai Smoke MAX 3-Wood',
    year: 2024,
    category: 'fairway-wood',
    launch: 'high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-25',
    swingSpeedRange: '80-100 mph',
    faceType: 'steel',
    keyTechnologies: ['Ai Smart Face', 'Jailbreak AI', 'Carbon Sole'],
    lofts: [15, 16.5, 18, 20, 23],
    shaftOptions: ['Project X Cypher 2.0', 'Mitsubishi Tensei AV Raw Blue'],
    priceRange: '$300-400',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['high launch', 'forgiveness', 'versatility'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'ping-g430-max-3wood-2023',
    brand: 'Ping',
    model: 'G430 MAX 3-Wood',
    year: 2023,
    category: 'fairway-wood',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 10,
    workability: 4,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-30',
    swingSpeedRange: '75-100 mph',
    faceType: 'steel',
    keyTechnologies: ['Carbon Crown', 'Spinsistency', 'Face Wrap Technology'],
    lofts: [14.5, 17.5, 20, 23, 26],
    shaftOptions: ['PING ALTA CB Black', 'Mitsubishi Tensei AV Raw White'],
    priceRange: '$280-380',
    distanceRating: 8,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['max forgiveness', 'high launch', 'slower swing speeds'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'titleist-tsr2-3wood-2023',
    brand: 'Titleist',
    model: 'TSR2 3-Wood',
    year: 2023,
    category: 'fairway-wood',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-15',
    swingSpeedRange: '90-115 mph',
    faceType: 'steel',
    keyTechnologies: ['Multi-Plateau VFT Face', 'Optimized CG', 'SureFit Hosel'],
    lofts: [13.5, 15, 16.5, 18, 21],
    shaftOptions: ['Project X HZRDUS Black', 'Mitsubishi Tensei AV Raw Blue'],
    priceRange: '$300-400',
    distanceRating: 9,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['distance', 'versatility', 'better players'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'titleist-tsr3-3wood-2023',
    brand: 'Titleist',
    model: 'TSR3 3-Wood',
    year: 2023,
    category: 'fairway-wood',
    launch: 'mid',
    spin: 'mid-low',
    forgiveness: 6,
    workability: 9,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-8',
    swingSpeedRange: '100-120 mph',
    faceType: 'steel',
    keyTechnologies: ['SureFit CG Track', 'ATI 425 Face', 'Adjustable Weights'],
    lofts: [13.5, 15],
    shaftOptions: ['Project X HZRDUS Black', 'Mitsubishi Diamana D+ Limited'],
    priceRange: '$350-450',
    distanceRating: 8,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['workability', 'low spin', 'tour players'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'cobra-darkspeed-max-3wood-2024',
    brand: 'Cobra',
    model: 'DARKSPEED MAX 3-Wood',
    year: 2024,
    category: 'fairway-wood',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 9,
    workability: 5,
    bias: 'draw',
    playerType: 'game-improvement',
    handicapRange: '10-30',
    swingSpeedRange: '75-100 mph',
    faceType: 'steel',
    keyTechnologies: ['PWRShell Face', 'H.O.T Face AI', 'Draw Bias Rails'],
    lofts: [15, 16.5, 18, 21],
    shaftOptions: ['Project X Cypher', 'Arccos Caddie Smart Grips'],
    priceRange: '$250-350',
    distanceRating: 8,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['draw bias', 'forgiveness', 'value'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'mizuno-st-max-230-3wood-2023',
    brand: 'Mizuno',
    model: 'ST-MAX 230 3-Wood',
    year: 2023,
    category: 'fairway-wood',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-25',
    swingSpeedRange: '80-100 mph',
    faceType: 'steel',
    keyTechnologies: ['Cortech Chamber', 'Beta Titanium Face', 'Carbon Crown'],
    lofts: [13.5, 15, 18, 21],
    shaftOptions: ['MFUSION M', 'Project X HZRDUS Smoke'],
    priceRange: '$250-350',
    distanceRating: 8,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['high launch', 'forgiveness', 'feel'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'srixon-zx-mkii-3wood-2023',
    brand: 'Srixon',
    model: 'ZX MK II 3-Wood',
    year: 2023,
    category: 'fairway-wood',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-18',
    swingSpeedRange: '90-110 mph',
    faceType: 'steel',
    keyTechnologies: ['Rebound Frame', 'Carbon Crown', 'Speed Pocket'],
    lofts: [13.5, 15, 18],
    shaftOptions: ['Project X HZRDUS Smoke', 'Mitsubishi Diamana PD'],
    priceRange: '$270-370',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'versatility', 'mid-handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-03'
  },
  {
    id: 'cleveland-launcher-xl2-3wood-2023',
    brand: 'Cleveland',
    model: 'Launcher XL2 3-Wood',
    year: 2023,
    category: 'fairway-wood',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 10,
    workability: 4,
    bias: 'draw',
    playerType: 'super-game-improvement',
    handicapRange: '15-36',
    swingSpeedRange: '65-90 mph',
    faceType: 'steel',
    keyTechnologies: ['Rebound Frame', 'Action Mass CB', 'Draw Bias Design'],
    lofts: [15, 18, 21, 24],
    shaftOptions: ['Miyazaki KAILI', 'Project X Cypher'],
    priceRange: '$200-280',
    distanceRating: 7,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['max forgiveness', 'slice fix', 'beginners'],
    notIdealFor: ['low handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'taylormade-stealth-2-hd-3wood-2023',
    brand: 'TaylorMade',
    model: 'Stealth 2 HD 3-Wood',
    year: 2023,
    category: 'fairway-wood',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 9,
    workability: 5,
    bias: 'draw',
    playerType: 'game-improvement',
    handicapRange: '10-30',
    swingSpeedRange: '75-100 mph',
    faceType: 'steel',
    keyTechnologies: ['Carbon Crown', 'V Steel Sole', 'Draw Bias'],
    lofts: [16, 19, 22],
    shaftOptions: ['Fujikura Ventus TR Red', 'Project X HZRDUS Smoke'],
    priceRange: '$280-380',
    distanceRating: 8,
    feelRating: 7,
    soundRating: 8,
    bestFor: ['draw bias', 'high launch', 'slice correction'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'callaway-rogue-st-max-3wood-2022',
    brand: 'Callaway',
    model: 'Rogue ST MAX 3-Wood',
    year: 2022,
    category: 'fairway-wood',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 6,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '5-20',
    swingSpeedRange: '85-105 mph',
    faceType: 'steel',
    keyTechnologies: ['Jailbreak AI Speed Frame', 'A.I. Face', 'Triaxial Carbon Crown'],
    lofts: [13.5, 15, 16.5, 18, 21],
    shaftOptions: ['Project X Cypher', 'Mitsubishi Tensei AV Raw'],
    priceRange: '$250-350',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'versatility', 'all-around performance'],
    isCurrentModel: false,
    releaseDate: '2022-02'
  },
  {
    id: 'ping-g430-sft-3wood-2023',
    brand: 'Ping',
    model: 'G430 SFT 3-Wood',
    year: 2023,
    category: 'fairway-wood',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 10,
    workability: 3,
    bias: 'draw',
    playerType: 'super-game-improvement',
    handicapRange: '15-36',
    swingSpeedRange: '70-95 mph',
    faceType: 'steel',
    keyTechnologies: ['Draw Biased Weighting', 'Carbon Crown', 'Face Wrap'],
    lofts: [16, 19, 22],
    shaftOptions: ['PING ALTA CB Slate', 'PING Tour 2.0 Chrome'],
    priceRange: '$280-380',
    distanceRating: 7,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['max draw bias', 'slice correction', 'high handicappers'],
    notIdealFor: ['draw hitters', 'low handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'cobra-ltdx-3wood-2022',
    brand: 'Cobra',
    model: 'LTDx 3-Wood',
    year: 2022,
    category: 'fairway-wood',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-15',
    swingSpeedRange: '90-110 mph',
    faceType: 'steel',
    keyTechnologies: ['H.O.T Face', 'PWRShell', 'Carbon Wrap Crown'],
    lofts: [13.5, 15, 16.5],
    shaftOptions: ['Project X HZRDUS Gen4', 'Mitsubishi Tensei AV'],
    priceRange: '$250-350',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'versatility', 'value'],
    isCurrentModel: false,
    releaseDate: '2022-01'
  },
  {
    id: 'wilson-dynapower-3wood-2023',
    brand: 'Wilson',
    model: 'Dynapower 3-Wood',
    year: 2023,
    category: 'fairway-wood',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-30',
    swingSpeedRange: '75-100 mph',
    faceType: 'steel',
    keyTechnologies: ['AI-Designed Face', 'PKR-2 Launch Zone', 'Power Hole Rails'],
    lofts: [15, 18, 21],
    shaftOptions: ['UST Mamiya Helium', 'KBS TD'],
    priceRange: '$200-300',
    distanceRating: 8,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['value', 'forgiveness', 'beginners'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'tour-edge-exotics-e723-3wood-2023',
    brand: 'Tour Edge',
    model: 'Exotics E723 3-Wood',
    year: 2023,
    category: 'fairway-wood',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 6,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '8-22',
    swingSpeedRange: '85-105 mph',
    faceType: 'steel',
    keyTechnologies: ['Diamond Face 2.0', 'Ridgeback Technology', 'Carbon Fiber Crown'],
    lofts: [13.5, 15, 17, 19],
    shaftOptions: ['Mitsubishi Tensei AV', 'Project X HZRDUS'],
    priceRange: '$220-320',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'value', 'mid-handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
];

/**
 * HYBRIDS
 */
export const HYBRID_SPECS: ClubSpecs[] = [
  {
    id: 'taylormade-qi10-hybrid-2024',
    brand: 'TaylorMade',
    model: 'Qi10 Rescue',
    year: 2024,
    category: 'hybrid',
    launch: 'high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '8-25',
    swingSpeedRange: '80-100 mph',
    faceType: 'steel',
    keyTechnologies: ['Twist Face', 'Speed Pocket', 'V Steel Sole'],
    lofts: [17, 19, 22, 25, 28, 31],
    shaftOptions: ['Fujikura Speeder NX', 'Project X HZRDUS Black'],
    priceRange: '$250-330',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 8,
    bestFor: ['forgiveness', 'versatility', 'long iron replacement'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'callaway-paradym-ai-smoke-hybrid-2024',
    brand: 'Callaway',
    model: 'Paradym Ai Smoke Hybrid',
    year: 2024,
    category: 'hybrid',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 6,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-18',
    swingSpeedRange: '85-105 mph',
    faceType: 'steel',
    keyTechnologies: ['Ai Smart Face', 'Jailbreak AI', 'Flash Face SS21'],
    lofts: [18, 20, 23, 26, 29],
    shaftOptions: ['Project X Cypher 2.0', 'Mitsubishi Tensei AV Raw Blue'],
    priceRange: '$270-350',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'versatility', 'iron-like trajectory'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'ping-g430-hybrid-2023',
    brand: 'Ping',
    model: 'G430 Hybrid',
    year: 2023,
    category: 'hybrid',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 10,
    workability: 4,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-30',
    swingSpeedRange: '75-100 mph',
    faceType: 'steel',
    keyTechnologies: ['Face Wrap', 'Spinsistency', 'PurFlex Cavity'],
    lofts: [17, 19, 22, 26, 30, 34],
    shaftOptions: ['PING ALTA CB Black', 'PING Tour 2.0 Chrome'],
    priceRange: '$250-330',
    distanceRating: 8,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['max forgiveness', 'high launch', 'slower swing speeds'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'titleist-tsr2-hybrid-2023',
    brand: 'Titleist',
    model: 'TSR2 Hybrid',
    year: 2023,
    category: 'hybrid',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-15',
    swingSpeedRange: '90-115 mph',
    faceType: 'steel',
    keyTechnologies: ['Active Recoil Channel 4.0', 'Optimized CG', 'SureFit Hosel'],
    lofts: [18, 21, 24, 27],
    shaftOptions: ['Project X HZRDUS Black', 'Mitsubishi Tensei AV Raw Blue'],
    priceRange: '$280-360',
    distanceRating: 9,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['distance', 'versatility', 'tour-level performance'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'cobra-darkspeed-hybrid-2024',
    brand: 'Cobra',
    model: 'DARKSPEED Hybrid',
    year: 2024,
    category: 'hybrid',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 6,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '8-22',
    swingSpeedRange: '85-105 mph',
    faceType: 'steel',
    keyTechnologies: ['H.O.T Face AI', 'PWRShell Face', 'Baffler Rails'],
    lofts: [17, 19, 21, 24, 27],
    shaftOptions: ['Project X Cypher', 'Arccos Caddie Smart Grips'],
    priceRange: '$220-300',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['versatility', 'value', 'distance'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'mizuno-st-max-230-hybrid-2023',
    brand: 'Mizuno',
    model: 'ST-MAX 230 Hybrid',
    year: 2023,
    category: 'hybrid',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-25',
    swingSpeedRange: '80-100 mph',
    faceType: 'steel',
    keyTechnologies: ['Cortech Chamber', 'Beta Rich Titanium', 'Wave Technology'],
    lofts: [18, 21, 24, 27],
    shaftOptions: ['MFUSION M', 'Project X HZRDUS Smoke'],
    priceRange: '$220-300',
    distanceRating: 8,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['forgiveness', 'feel', 'high launch'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'srixon-zx-mkii-hybrid-2023',
    brand: 'Srixon',
    model: 'ZX MK II Hybrid',
    year: 2023,
    category: 'hybrid',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-18',
    swingSpeedRange: '90-110 mph',
    faceType: 'steel',
    keyTechnologies: ['Rebound Frame', 'Carbon Crown', 'Mainframe'],
    lofts: [17, 19, 22, 25],
    shaftOptions: ['Project X HZRDUS Smoke', 'Mitsubishi Diamana PD'],
    priceRange: '$240-320',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'iron-like feel', 'versatility'],
    isCurrentModel: true,
    releaseDate: '2023-03'
  },
  {
    id: 'cleveland-launcher-xl2-hybrid-2023',
    brand: 'Cleveland',
    model: 'Launcher XL2 Halo Hybrid',
    year: 2023,
    category: 'hybrid',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 10,
    workability: 3,
    bias: 'draw',
    playerType: 'super-game-improvement',
    handicapRange: '18-36',
    swingSpeedRange: '65-90 mph',
    faceType: 'steel',
    keyTechnologies: ['Rebound Frame', 'Action Mass CB', 'Draw Bias Rails'],
    lofts: [19, 22, 25, 28, 31],
    shaftOptions: ['Miyazaki KAILI', 'Project X Cypher'],
    priceRange: '$180-260',
    distanceRating: 7,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['max forgiveness', 'slice fix', 'beginners'],
    notIdealFor: ['low handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'taylormade-stealth-2-rescue-2023',
    brand: 'TaylorMade',
    model: 'Stealth 2 Rescue',
    year: 2023,
    category: 'hybrid',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 6,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '5-20',
    swingSpeedRange: '85-105 mph',
    faceType: 'steel',
    keyTechnologies: ['Twist Face', 'Speed Pocket', 'V Steel Sole'],
    lofts: [17, 19, 22, 25, 28],
    shaftOptions: ['Fujikura Ventus TR Red', 'Project X HZRDUS Smoke'],
    priceRange: '$250-330',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 8,
    bestFor: ['distance', 'versatility', 'off-the-deck playability'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'callaway-apex-utility-2024',
    brand: 'Callaway',
    model: 'Apex UW',
    year: 2024,
    category: 'hybrid',
    launch: 'mid',
    spin: 'mid',
    forgiveness: 6,
    workability: 8,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-12',
    swingSpeedRange: '95-115 mph',
    faceType: 'steel',
    keyTechnologies: ['AI Flash Face', 'Tungsten Weighting', 'Forged Construction'],
    lofts: [18, 21, 24],
    shaftOptions: ['Project X LZ', 'KBS Tour Hybrid'],
    priceRange: '$280-360',
    distanceRating: 8,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['iron-like performance', 'workability', 'tour players'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'ping-g430-crossover-2023',
    brand: 'Ping',
    model: 'G430 Crossover',
    year: 2023,
    category: 'hybrid',
    launch: 'mid',
    spin: 'mid-low',
    forgiveness: 6,
    workability: 8,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-12',
    swingSpeedRange: '95-115 mph',
    faceType: 'steel',
    keyTechnologies: ['Maraging Steel Face', 'Hollow Construction', 'Thermo-Plastic Polymer'],
    lofts: [18, 20, 23],
    shaftOptions: ['PING Tour 2.0 Chrome', 'Nippon Modus3 Tour'],
    priceRange: '$250-330',
    distanceRating: 8,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['low flight', 'iron-like trajectory', 'shot shaping'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'titleist-u505-hybrid-2023',
    brand: 'Titleist',
    model: 'U-505',
    year: 2023,
    category: 'hybrid',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 6,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '5-20',
    swingSpeedRange: '85-105 mph',
    faceType: 'steel',
    keyTechnologies: ['Max Impact 2.0', 'Super Strong SUP10 Steel', 'SureFit Hosel'],
    lofts: [21, 24, 27, 30],
    shaftOptions: ['Project X HZRDUS Black', 'Mitsubishi Tensei AV Blue'],
    priceRange: '$280-360',
    distanceRating: 8,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['forgiveness', 'versatility', 'iron-replacement'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'wilson-dynapower-hybrid-2023',
    brand: 'Wilson',
    model: 'Dynapower Hybrid',
    year: 2023,
    category: 'hybrid',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '12-30',
    swingSpeedRange: '75-100 mph',
    faceType: 'steel',
    keyTechnologies: ['AI-Designed Face', 'PKR-2 Launch Zone', 'Power Hole Rails'],
    lofts: [19, 22, 25, 28],
    shaftOptions: ['UST Mamiya Helium', 'KBS TD'],
    priceRange: '$150-230',
    distanceRating: 8,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['value', 'forgiveness', 'beginners'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'tour-edge-exotics-e723-hybrid-2023',
    brand: 'Tour Edge',
    model: 'Exotics E723 Hybrid',
    year: 2023,
    category: 'hybrid',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 6,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '8-22',
    swingSpeedRange: '85-105 mph',
    faceType: 'steel',
    keyTechnologies: ['Diamond Face 2.0', 'Ridgeback Technology', 'Carbon Fiber Crown'],
    lofts: [17, 19, 22, 25],
    shaftOptions: ['Mitsubishi Tensei AV', 'Project X HZRDUS'],
    priceRange: '$180-260',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'value', 'versatility'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'taylormade-p-udi-2023',
    brand: 'TaylorMade',
    model: 'P-UDI',
    year: 2023,
    category: 'hybrid',
    launch: 'mid-low',
    spin: 'low',
    forgiveness: 5,
    workability: 9,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-8',
    swingSpeedRange: '100-120 mph',
    faceType: 'steel',
    keyTechnologies: ['Forged Construction', 'Hollow Body', 'SpeedFoam Air'],
    lofts: [17, 20, 23],
    shaftOptions: ['Project X HZRDUS Black', 'KBS Tour'],
    priceRange: '$200-280',
    distanceRating: 7,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['low flight', 'control', 'shot shaping'],
    notIdealFor: ['high handicappers', 'slow swingers'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
];

/**
 * IRON SETS
 */
export const IRON_SET_SPECS: ClubSpecs[] = [
  {
    id: 'taylormade-p790-2023',
    brand: 'TaylorMade',
    model: 'P790',
    year: 2023,
    category: 'iron-set',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-15',
    swingSpeedRange: '85-105 mph',
    headType: 'hollow-body',
    faceType: 'forged',
    keyTechnologies: ['SpeedFoam Air', 'Forged L-Cup Face', 'Tungsten Weighting'],
    shaftOptions: ['KBS Tour', 'Project X LZ', 'Nippon Modus 120'],
    priceRange: '$1,400-1,600',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'forgiveness with feel', 'better players'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'callaway-apex-2023',
    brand: 'Callaway',
    model: 'Apex',
    year: 2023,
    category: 'iron-set',
    launch: 'mid',
    spin: 'mid-high',
    forgiveness: 7,
    workability: 8,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-10',
    swingSpeedRange: '90-110 mph',
    headType: 'cavity-back',
    faceType: 'forged',
    keyTechnologies: ['Forged 1025 Carbon Steel', 'AI Flash Face Cup', 'Tungsten Energy Core'],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour', 'Project X LZ'],
    priceRange: '$1,400-1,600',
    distanceRating: 7,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['feel', 'workability', 'single-digit handicaps'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'ping-g430-2023',
    brand: 'Ping',
    model: 'G430',
    year: 2023,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 10,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-25',
    swingSpeedRange: '75-95 mph',
    headType: 'cavity-back',
    faceType: 'steel',
    keyTechnologies: ['PurFlex Cavity Badge', 'Toe Wrap Construction', 'Multi-Material Construction'],
    shaftOptions: ['PING AWT 2.0', 'PING Alta CB', 'Steel or Graphite'],
    priceRange: '$1,000-1,200',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['forgiveness', 'high launch', 'slower swing speeds'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'titleist-t200-2023',
    brand: 'Titleist',
    model: 'T200',
    year: 2023,
    category: 'iron-set',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-15',
    swingSpeedRange: '85-105 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['Max Impact 2.0', 'Engineered Muscle Plate', 'D18 Tungsten'],
    shaftOptions: ['True Temper Dynamic Gold', 'Project X LZ', 'Nippon Modus'],
    priceRange: '$1,400-1,600',
    distanceRating: 8,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance with control', 'mid-handicappers', 'consistent ball striking'],
    isCurrentModel: true,
    releaseDate: '2023-03'
  },
  // MORE IRON SETS - 2024 MODELS
  {
    id: 'taylormade-qi-irons-2024',
    brand: 'TaylorMade',
    model: 'Qi Irons',
    year: 2024,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid',
    forgiveness: 10,
    workability: 4,
    bias: 'slight-draw',
    playerType: 'super-game-improvement',
    handicapRange: '15-36',
    swingSpeedRange: '70-95 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['SpeedFoam Air', 'Cap Back Design', '63 Face Slots'],
    shaftOptions: ['UST Mamiya Recoil', 'KBS Max 80', 'PING ALTA CB Slate'],
    priceRange: '$1,000-1,200',
    distanceRating: 10,
    feelRating: 6,
    soundRating: 7,
    bestFor: ['max forgiveness', 'high launch', 'beginners'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'callaway-paradym-ai-smoke-irons-2024',
    brand: 'Callaway',
    model: 'Paradym Ai Smoke',
    year: 2024,
    category: 'iron-set',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 6,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '8-20',
    swingSpeedRange: '80-100 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['AI Smart Face', 'Speed Frame', 'Urethane Microspheres'],
    shaftOptions: ['True Temper Elevate', 'UST Mamiya Recoil', 'Nippon Modus'],
    priceRange: '$1,300-1,500',
    distanceRating: 9,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['distance', 'forgiveness', 'mid-handicappers'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'callaway-apex-pro-2024',
    brand: 'Callaway',
    model: 'Apex Pro 24',
    year: 2024,
    category: 'iron-set',
    launch: 'mid',
    spin: 'mid-high',
    forgiveness: 6,
    workability: 9,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-8',
    swingSpeedRange: '95-115 mph',
    headType: 'cavity-back',
    faceType: 'forged',
    keyTechnologies: ['1025 Carbon Steel', 'AI Flash Face Cup', 'Precision Weighting'],
    shaftOptions: ['True Temper Dynamic Gold', 'Project X LZ', 'Nippon Modus3'],
    priceRange: '$1,500-1,700',
    distanceRating: 7,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['feel', 'workability', 'tour players'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'ping-i230-2023',
    brand: 'Ping',
    model: 'i230',
    year: 2023,
    category: 'iron-set',
    launch: 'mid',
    spin: 'mid-high',
    forgiveness: 7,
    workability: 8,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-12',
    swingSpeedRange: '90-110 mph',
    headType: 'cavity-back',
    faceType: 'forged',
    keyTechnologies: ['Hydropearl 2.0', 'Activated Elastomer', 'Progressive Sole'],
    shaftOptions: ['True Temper Dynamic Gold', 'Project X LZ', 'Nippon Modus3 Tour'],
    priceRange: '$1,300-1,500',
    distanceRating: 7,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['feel', 'control', 'better players'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'ping-g730-2024',
    brand: 'Ping',
    model: 'G730',
    year: 2024,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid',
    forgiveness: 10,
    workability: 4,
    bias: 'slight-draw',
    playerType: 'super-game-improvement',
    handicapRange: '18-36',
    swingSpeedRange: '65-90 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['VFT Face', 'Ascending Face Flex', 'Deep CG'],
    shaftOptions: ['PING ALTA CB Slate', 'UST Mamiya Recoil', 'Graphite Options'],
    priceRange: '$1,100-1,300',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['max forgiveness', 'slow swing speeds', 'beginners'],
    isCurrentModel: true,
    releaseDate: '2024-03'
  },
  {
    id: 'titleist-t100-2023',
    brand: 'Titleist',
    model: 'T100',
    year: 2023,
    category: 'iron-set',
    launch: 'mid',
    spin: 'mid-high',
    forgiveness: 5,
    workability: 10,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-6',
    swingSpeedRange: '95-120 mph',
    headType: 'muscle-back',
    faceType: 'forged',
    keyTechnologies: ['Continuous Face Radius', 'Multi-Density Muscle', 'D18 Tungsten'],
    shaftOptions: ['True Temper Dynamic Gold Tour Issue', 'Project X LZ', 'Nippon Modus3 Tour'],
    priceRange: '$1,500-1,700',
    distanceRating: 6,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['workability', 'feel', 'tour players'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'titleist-t350-2023',
    brand: 'Titleist',
    model: 'T350',
    year: 2023,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '12-28',
    swingSpeedRange: '75-95 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['Max Impact 2.0', 'Progressive Blade Lengths', 'D18 Tungsten'],
    shaftOptions: ['True Temper Elevate', 'UST Mamiya Recoil', 'Nippon Modus3'],
    priceRange: '$1,200-1,400',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['forgiveness', 'distance', 'high handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'cobra-darkspeed-irons-2024',
    brand: 'Cobra',
    model: 'DARKSPEED',
    year: 2024,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-25',
    swingSpeedRange: '80-100 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['H.O.T Face AI', 'PWRShell Face', 'PWRSHELL Technology'],
    shaftOptions: ['KBS Tour Lite', 'UST Mamiya Recoil', 'Arccos Smart Grips'],
    priceRange: '$900-1,100',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['value', 'distance', 'forgiveness'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'mizuno-pro-243-2024',
    brand: 'Mizuno',
    model: 'Pro 243',
    year: 2024,
    category: 'iron-set',
    launch: 'mid',
    spin: 'mid-high',
    forgiveness: 7,
    workability: 8,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-15',
    swingSpeedRange: '90-110 mph',
    headType: 'cavity-back',
    faceType: 'forged',
    keyTechnologies: ['Chromoly Face', 'Grain Flow Forging HD', 'Harmonic Impact Technology'],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour', 'Nippon Modus3'],
    priceRange: '$1,400-1,600',
    distanceRating: 8,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['feel', 'mid-handicappers', 'Mizuno loyalists'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'mizuno-pro-225-2022',
    brand: 'Mizuno',
    model: 'Pro 225',
    year: 2022,
    category: 'iron-set',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-18',
    swingSpeedRange: '85-105 mph',
    headType: 'hollow-body',
    faceType: 'forged',
    keyTechnologies: ['Chromoly Face', 'Grain Flow Forging', 'Harmonic Impact Technology'],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour', 'Nippon Modus3'],
    priceRange: '$1,300-1,500',
    distanceRating: 9,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['distance with feel', 'mid-handicappers', 'soft feel seekers'],
    isCurrentModel: false,
    releaseDate: '2022-09'
  },
  {
    id: 'mizuno-jpx-923-hot-metal-2023',
    brand: 'Mizuno',
    model: 'JPX 923 Hot Metal',
    year: 2023,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-28',
    swingSpeedRange: '75-100 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['Chromoly Face', 'Stability Frame', 'Seamless Cup Face'],
    shaftOptions: ['UST Mamiya Recoil', 'KBS Tour Lite', 'Nippon Modus3'],
    priceRange: '$1,000-1,200',
    distanceRating: 10,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['max distance', 'forgiveness', 'high handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'srixon-zx5-mkii-irons-2023',
    brand: 'Srixon',
    model: 'ZX5 MK II',
    year: 2023,
    category: 'iron-set',
    launch: 'mid-high',
    spin: 'mid',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-18',
    swingSpeedRange: '85-105 mph',
    headType: 'cavity-back',
    faceType: 'forged',
    keyTechnologies: ['MainFrame', 'V.T. Sole', 'Tour VT Sole'],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour', 'Nippon Modus3'],
    priceRange: '$1,200-1,400',
    distanceRating: 8,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['distance with feel', 'mid-handicappers', 'workability'],
    isCurrentModel: true,
    releaseDate: '2023-03'
  },
  {
    id: 'srixon-zx7-mkii-irons-2023',
    brand: 'Srixon',
    model: 'ZX7 MK II',
    year: 2023,
    category: 'iron-set',
    launch: 'mid',
    spin: 'mid-high',
    forgiveness: 6,
    workability: 9,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-10',
    swingSpeedRange: '95-115 mph',
    headType: 'muscle-back',
    faceType: 'forged',
    keyTechnologies: ['MainFrame', 'Tour V.T. Sole', 'Continuous Muscle'],
    shaftOptions: ['True Temper Dynamic Gold Tour Issue', 'KBS Tour', 'Nippon Modus3 Tour'],
    priceRange: '$1,300-1,500',
    distanceRating: 7,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['feel', 'workability', 'low handicappers'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-03'
  },
  {
    id: 'cleveland-launcher-xl2-irons-2023',
    brand: 'Cleveland',
    model: 'Launcher XL2',
    year: 2023,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 10,
    workability: 3,
    bias: 'draw',
    playerType: 'super-game-improvement',
    handicapRange: '18-36',
    swingSpeedRange: '65-90 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['MainFrame', 'V.T. Sole', 'HiBore Crown'],
    shaftOptions: ['UST Mamiya Recoil', 'Miyazaki KAILI', 'Graphite Options'],
    priceRange: '$700-900',
    distanceRating: 9,
    feelRating: 6,
    soundRating: 6,
    bestFor: ['max forgiveness', 'beginners', 'slow swing speeds'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'wilson-dynapower-irons-2023',
    brand: 'Wilson',
    model: 'Dynapower',
    year: 2023,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '12-30',
    swingSpeedRange: '75-100 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['AI-Designed Face', 'PKR-2 Launch Zone', 'Power Hole Rails'],
    shaftOptions: ['UST Mamiya Helium', 'KBS Tour Lite', 'Steel/Graphite'],
    priceRange: '$600-800',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['value', 'forgiveness', 'beginners'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'tour-edge-exotics-c723-irons-2023',
    brand: 'Tour Edge',
    model: 'Exotics C723',
    year: 2023,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-25',
    swingSpeedRange: '80-100 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['Diamond Face 2.0', 'SpinFlex Face', 'Diamond Face VFT'],
    shaftOptions: ['KBS Max', 'UST Mamiya Recoil', 'Mitsubishi Tensei'],
    priceRange: '$800-1,000',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['value', 'distance', 'mid-handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'taylormade-p770-2023',
    brand: 'TaylorMade',
    model: 'P770',
    year: 2023,
    category: 'iron-set',
    launch: 'mid',
    spin: 'mid-high',
    forgiveness: 6,
    workability: 9,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-10',
    swingSpeedRange: '95-115 mph',
    headType: 'hollow-body',
    faceType: 'forged',
    keyTechnologies: ['SpeedFoam Air', 'Forged Face', 'Tungsten Weighting'],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour', 'Project X LZ'],
    priceRange: '$1,400-1,600',
    distanceRating: 7,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['feel', 'workability', 'tour players'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'taylormade-stealth-irons-2023',
    brand: 'TaylorMade',
    model: 'Stealth',
    year: 2023,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid',
    forgiveness: 9,
    workability: 5,
    bias: 'slight-draw',
    playerType: 'game-improvement',
    handicapRange: '10-25',
    swingSpeedRange: '80-100 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['Cap Back Design', 'Inverted Cone Technology', 'Echo Damping System'],
    shaftOptions: ['KBS Max 85', 'UST Mamiya Recoil', 'Fujikura Ventus'],
    priceRange: '$1,000-1,200',
    distanceRating: 9,
    feelRating: 7,
    soundRating: 8,
    bestFor: ['distance', 'forgiveness', 'mid-handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'callaway-big-bertha-2023',
    brand: 'Callaway',
    model: 'Big Bertha',
    year: 2023,
    category: 'iron-set',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 10,
    workability: 3,
    bias: 'draw',
    playerType: 'super-game-improvement',
    handicapRange: '18-36',
    swingSpeedRange: '65-90 mph',
    headType: 'hollow-body',
    faceType: 'multi-material',
    keyTechnologies: ['Flash Face AI', 'Tungsten Energy Core', 'Urethane Microspheres'],
    shaftOptions: ['UST Mamiya Recoil', 'KBS Max 80', 'Graphite Options'],
    priceRange: '$900-1,100',
    distanceRating: 9,
    feelRating: 6,
    soundRating: 7,
    bestFor: ['max forgiveness', 'beginners', 'seniors'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
];

/**
 * WEDGES
 */
export const WEDGE_SPECS: ClubSpecs[] = [
  {
    id: 'vokey-sm9-2023',
    brand: 'Titleist',
    model: 'Vokey SM9',
    year: 2023,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 6,
    workability: 10,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-15',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['Progressive CG', 'Spin Milled Grooves', '6 Grind Options'],
    lofts: [46, 48, 50, 52, 54, 56, 58, 60, 62],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Hi-Rev 2.0'],
    priceRange: '$180-200',
    distanceRating: 6,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['spin control', 'versatility', 'short game precision'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'cleveland-rtx-zipcore-2023',
    brand: 'Cleveland',
    model: 'RTX ZipCore',
    year: 2023,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 7,
    workability: 8,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '5-20',
    swingSpeedRange: 'all',
    headType: 'cavity-back',
    faceType: 'forged',
    keyTechnologies: ['ZipCore Technology', 'UltiZip Grooves', 'Heat Treatment'],
    lofts: [46, 48, 50, 52, 54, 56, 58, 60],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour'],
    priceRange: '$150-170',
    distanceRating: 6,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['spin', 'forgiveness', 'all-around performance'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'callaway-jaws-raw-2023',
    brand: 'Callaway',
    model: 'JAWS RAW',
    year: 2023,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 7,
    workability: 9,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-15',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['RAW Face', '37 Grooves', 'Offset Grooves'],
    lofts: [50, 52, 54, 56, 58, 60],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Hi-Rev'],
    priceRange: '$160-180',
    distanceRating: 6,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['max spin', 'shot making', 'soft conditions'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  // MORE WEDGES
  {
    id: 'taylormade-milled-grind-4-2024',
    brand: 'TaylorMade',
    model: 'MG4',
    year: 2024,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 7,
    workability: 9,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-15',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['ZTP RAW Face', 'Milled Grooves', 'Raised Micro Ribs'],
    lofts: [46, 48, 50, 52, 54, 56, 58, 60],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Hi-Rev 2.0'],
    priceRange: '$160-180',
    distanceRating: 6,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['spin control', 'versatility', 'tour players'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'taylormade-hi-toe-3-2023',
    brand: 'TaylorMade',
    model: 'Hi-Toe 3',
    year: 2023,
    category: 'wedge',
    launch: 'high',
    spin: 'high',
    forgiveness: 8,
    workability: 8,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '5-20',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['Full Face Grooves', 'High Toe Design', 'RAW Face'],
    lofts: [54, 56, 58, 60],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour'],
    priceRange: '$170-190',
    distanceRating: 6,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['open face shots', 'bunker play', 'versatility'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'ping-glide-4-0-2023',
    brand: 'Ping',
    model: 'Glide 4.0',
    year: 2023,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 8,
    workability: 7,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '5-25',
    swingSpeedRange: 'all',
    headType: 'cavity-back',
    faceType: 'forged',
    keyTechnologies: ['Precision Milled Grooves', 'Hydropearl 2.0', 'Empower Sole'],
    lofts: [46, 50, 52, 54, 56, 58, 60],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour', 'PING Z-Z115'],
    priceRange: '$160-180',
    distanceRating: 6,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['consistency', 'forgiveness', 'all-around performance'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'titleist-vokey-sm10-2024',
    brand: 'Titleist',
    model: 'Vokey SM10',
    year: 2024,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 6,
    workability: 10,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-12',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['Progressive CG', 'Spin Milled Grooves', '6 Grind Options'],
    lofts: [46, 48, 50, 52, 54, 56, 58, 60, 62],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Hi-Rev 2.0', 'Project X LZ'],
    priceRange: '$180-200',
    distanceRating: 6,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['max spin control', 'shot variety', 'tour players'],
    isCurrentModel: true,
    releaseDate: '2024-09'
  },
  {
    id: 'cobra-king-mim-2023',
    brand: 'Cobra',
    model: 'King MIM',
    year: 2023,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 7,
    workability: 8,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-18',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['Metal Injection Molded', 'Snakebite Grooves', 'Versatile Grind'],
    lofts: [50, 52, 54, 56, 58, 60],
    shaftOptions: ['KBS Tour', 'True Temper Dynamic Gold'],
    priceRange: '$150-170',
    distanceRating: 6,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['value', 'spin', 'versatility'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'mizuno-t24-2024',
    brand: 'Mizuno',
    model: 'T24',
    year: 2024,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 6,
    workability: 10,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-12',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['Grain Flow Forged HD', 'HydroFlow Micro Grooves', 'Quad Cut Grooves'],
    lofts: [46, 48, 50, 52, 54, 56, 58, 60],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour', 'Nippon Modus3 Wedge'],
    priceRange: '$170-190',
    distanceRating: 6,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['feel', 'precision', 'Mizuno loyalists'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'srixon-zx7-wedge-2023',
    brand: 'Srixon',
    model: 'ZX7',
    year: 2023,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 6,
    workability: 9,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '0-15',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['Rotational Milling', 'S20C Carbon Steel', 'Tour VT Sole'],
    lofts: [48, 50, 52, 54, 56, 58, 60],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour'],
    priceRange: '$150-170',
    distanceRating: 6,
    feelRating: 9,
    soundRating: 9,
    bestFor: ['spin', 'feel', 'value'],
    isCurrentModel: true,
    releaseDate: '2023-03'
  },
  {
    id: 'callaway-opus-2024',
    brand: 'Callaway',
    model: 'Opus',
    year: 2024,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 6,
    workability: 10,
    bias: 'neutral',
    playerType: 'tour',
    handicapRange: '0-10',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['8620 Carbon Steel', 'Milled Grooves', 'Premium Sound Ribs'],
    lofts: [48, 50, 52, 54, 56, 58, 60],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Hi-Rev 2.0'],
    priceRange: '$180-200',
    distanceRating: 6,
    feelRating: 10,
    soundRating: 10,
    bestFor: ['tour-level feel', 'workability', 'shot shaping'],
    notIdealFor: ['high handicappers'],
    isCurrentModel: true,
    releaseDate: '2024-02'
  },
  {
    id: 'wilson-staff-model-wedge-2023',
    brand: 'Wilson',
    model: 'Staff Model',
    year: 2023,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 7,
    workability: 8,
    bias: 'neutral',
    playerType: 'better-player',
    handicapRange: '5-18',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['8620 Carbon Steel', 'Milled Grooves', 'Multiple Grinds'],
    lofts: [48, 50, 52, 54, 56, 58, 60],
    shaftOptions: ['KBS Tour', 'True Temper Dynamic Gold'],
    priceRange: '$130-150',
    distanceRating: 6,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['value', 'spin', 'better players'],
    isCurrentModel: true,
    releaseDate: '2023-01'
  },
  {
    id: 'cleveland-rtx-full-face-2023',
    brand: 'Cleveland',
    model: 'RTX Full Face',
    year: 2023,
    category: 'wedge',
    launch: 'mid-high',
    spin: 'high',
    forgiveness: 8,
    workability: 8,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '5-25',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['Full Face Grooves', 'ZipCore Technology', 'UltiZip Grooves'],
    lofts: [54, 56, 58, 60, 64],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour'],
    priceRange: '$150-170',
    distanceRating: 6,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['open face shots', 'bunker play', 'versatility'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
  {
    id: 'cleveland-cbx-4-zipcore-2024',
    brand: 'Cleveland',
    model: 'CBX 4 ZipCore',
    year: 2024,
    category: 'wedge',
    launch: 'high',
    spin: 'mid-high',
    forgiveness: 9,
    workability: 6,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '10-30',
    swingSpeedRange: 'all',
    headType: 'cavity-back',
    faceType: 'cast',
    keyTechnologies: ['ZipCore Technology', 'Cavity Back Design', 'Feel Balancing Technology'],
    lofts: [48, 50, 52, 54, 56, 58, 60],
    shaftOptions: ['True Temper Dynamic Gold', 'KBS Tour Lite'],
    priceRange: '$140-160',
    distanceRating: 7,
    feelRating: 7,
    soundRating: 7,
    bestFor: ['forgiveness', 'consistency', 'high handicappers'],
    isCurrentModel: true,
    releaseDate: '2024-01'
  },
  {
    id: 'tour-edge-exs-wedge-2023',
    brand: 'Tour Edge',
    model: 'EXS Pro',
    year: 2023,
    category: 'wedge',
    launch: 'mid',
    spin: 'high',
    forgiveness: 7,
    workability: 8,
    bias: 'neutral',
    playerType: 'game-improvement',
    handicapRange: '8-22',
    swingSpeedRange: 'all',
    headType: 'blade',
    faceType: 'forged',
    keyTechnologies: ['Vibration-Reducing Tech', 'Multi-Cut Grooves', 'Diamond Face'],
    lofts: [50, 52, 54, 56, 58, 60],
    shaftOptions: ['KBS Tour', 'True Temper Dynamic Gold'],
    priceRange: '$120-140',
    distanceRating: 6,
    feelRating: 8,
    soundRating: 8,
    bestFor: ['value', 'spin', 'mid-handicappers'],
    isCurrentModel: true,
    releaseDate: '2023-02'
  },
];

/**
 * UTILITY FUNCTIONS
 */

/**
 * Get club recommendations based on player profile
 */
export function getRecommendedClubs(
  category: ClubSpecs['category'],
  handicap: number,
  swingSpeed: number,
  missPattern?: 'left' | 'right' | 'straight',
  skillLevel?: string
): ClubSpecs[] {
  const allSpecs = [...DRIVER_SPECS, ...FAIRWAY_WOOD_SPECS, ...HYBRID_SPECS, ...IRON_SET_SPECS, ...WEDGE_SPECS];
  
  return allSpecs
    .filter(club => club.category === category)
    .filter(club => {
      // Filter by handicap range
      const [minHcp, maxHcp] = parseHandicapRange(club.handicapRange);
      return handicap >= minHcp && handicap <= maxHcp;
    })
    .filter(club => {
      // Filter by swing speed
      if (club.swingSpeedRange === 'all') return true;
      const [minSpeed, maxSpeed] = parseSwingSpeedRange(club.swingSpeedRange);
      return swingSpeed >= minSpeed && swingSpeed <= maxSpeed;
    })
    .filter(club => {
      // Filter by miss pattern (bias matching)
      if (!missPattern || missPattern === 'straight') return true;
      if (missPattern === 'right' && (club.bias === 'draw' || club.bias === 'slight-draw')) return true;
      if (missPattern === 'left' && (club.bias === 'fade' || club.bias === 'slight-fade')) return true;
      if (club.bias === 'neutral') return true;
      return false;
    })
    .sort((a, b) => {
      // Prioritize current models and higher forgiveness
      if (a.isCurrentModel && !b.isCurrentModel) return -1;
      if (!a.isCurrentModel && b.isCurrentModel) return 1;
      return b.forgiveness - a.forgiveness;
    })
    .slice(0, 5); // Return top 5 recommendations
}

/**
 * Parse handicap range string
 */
function parseHandicapRange(range: string): [number, number] {
  if (range.includes('+')) {
    const min = parseInt(range.replace('+', ''));
    return [min, 40];
  }
  const [min, max] = range.split('-').map(s => parseInt(s));
  return [min || 0, max || 40];
}

/**
 * Parse swing speed range string
 */
function parseSwingSpeedRange(range: string): [number, number] {
  const matches = range.match(/(\d+)-(\d+)/);
  if (matches) {
    return [parseInt(matches[1]), parseInt(matches[2])];
  }
  return [0, 200];
}

/**
 * Get club details by ID
 */
export function getClubSpecsById(clubId: string): ClubSpecs | undefined {
  const allSpecs = [...DRIVER_SPECS, ...FAIRWAY_WOOD_SPECS, ...HYBRID_SPECS, ...IRON_SET_SPECS, ...WEDGE_SPECS];
  return allSpecs.find(club => club.id === clubId);
}

/**
 * Search clubs by brand and model
 */
export function searchClubs(brand: string, model: string): ClubSpecs[] {
  const allSpecs = [...DRIVER_SPECS, ...FAIRWAY_WOOD_SPECS, ...HYBRID_SPECS, ...IRON_SET_SPECS, ...WEDGE_SPECS];
  return allSpecs.filter(club => 
    club.brand.toLowerCase().includes(brand.toLowerCase()) &&
    club.model.toLowerCase().includes(model.toLowerCase())
  );
}

/**
 * Get all clubs in a specific category
 */
export function getClubsByCategory(category: ClubSpecs['category']): ClubSpecs[] {
  const allSpecs = [...DRIVER_SPECS, ...FAIRWAY_WOOD_SPECS, ...HYBRID_SPECS, ...IRON_SET_SPECS, ...WEDGE_SPECS];
  return allSpecs.filter(club => club.category === category);
}

/**
 * Get all available brands
 */
export function getAvailableBrands(): string[] {
  const allSpecs = [...DRIVER_SPECS, ...FAIRWAY_WOOD_SPECS, ...HYBRID_SPECS, ...IRON_SET_SPECS, ...WEDGE_SPECS];
  const brands = allSpecs.map(club => club.brand);
  return Array.from(new Set(brands)).sort();
}

/**
 * Get all clubs by brand
 */
export function getClubsByBrand(brand: string): ClubSpecs[] {
  const allSpecs = [...DRIVER_SPECS, ...FAIRWAY_WOOD_SPECS, ...HYBRID_SPECS, ...IRON_SET_SPECS, ...WEDGE_SPECS];
  return allSpecs.filter(club => club.brand.toLowerCase() === brand.toLowerCase());
}

/**
 * Get total club count in database
 */
export function getTotalClubCount(): { drivers: number; fairways: number; hybrids: number; irons: number; wedges: number; total: number } {
  return {
    drivers: DRIVER_SPECS.length,
    fairways: FAIRWAY_WOOD_SPECS.length,
    hybrids: HYBRID_SPECS.length,
    irons: IRON_SET_SPECS.length,
    wedges: WEDGE_SPECS.length,
    total: DRIVER_SPECS.length + FAIRWAY_WOOD_SPECS.length + HYBRID_SPECS.length + IRON_SET_SPECS.length + WEDGE_SPECS.length
  };
}

